import { OrganizationResponseDto } from "@/lib/interfaces/organizations";
import { SmallAddIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { Save } from "lucide-react";
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { useUser } from "../../hooks/use-user";
import useApiRequest from "../../lib/hooks/useRequest";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../ui/components/ui/form";
import CompanySettings from "./CompanySettings";
import ConversionTracking from "./ConversionTracking";
import EmailSignature from "./EmailSignature";
import InternalMail from "./InternalMail";
import KandidatKI from "./KandidatKI";
import PrivacyPolicy from "./PrivacyPolicy";
import "./settings-styles.css";
import ThirdPartyApps from "./ThirdPartyApps";
import { getVisibleSettingsTabs, resolveSettingsTabId, SettingsTabContext } from "./settings-tabs";
import { UserDataTable } from "./UserDataTable";
import Webhooks from "./Webhooks";

const Settings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const { apiRequest } = useApiRequest();
  const { user: loggedInUser } = useUser();

  // Exactly one of the two flags is set for superadmins: showSuperadminPanel in their own
  // panel, showBackToSuperadmin while signed in as an organization.
  const isSuperAdmin = Boolean(loggedInUser?.showSuperadminPanel || loggedInUser?.showBackToSuperadmin);

  const [organization, setOrganization] = useState<OrganizationResponseDto>();

  const getOrganizationSettings = useCallback(async () => {
    const response = await apiRequest<OrganizationResponseDto>("organizations", "GET");

    if (response?.data) {
      setOrganization(response.data);
    } else {
      setOrganization(undefined);
    }
  }, [apiRequest]);

  useEffect(() => {
    getOrganizationSettings();
  }, [apiRequest, getOrganizationSettings]);

  const tabContext: SettingsTabContext | undefined = organization ? { organization, isSuperAdmin } : undefined;
  const visibleTabs = tabContext ? getVisibleSettingsTabs(tabContext) : [];
  const currentTabId = resolveSettingsTabId(searchParams.get("tab"), visibleTabs);
  const currentTabIndex = Math.max(
    visibleTabs.findIndex((tab) => tab.id === currentTabId),
    0,
  );

  const selectTab = useCallback(
    (tabId: string, replace: boolean) => {
      if (searchParams.get("tab") === tabId) return;

      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set("tab", tabId);
      setSearchParams(nextSearchParams, { replace });
    },
    [searchParams, setSearchParams],
  );

  // Rewrites an old numeric link, or a missing parameter, to the named form. Replace
  // rather than push, so the back button does not land on the address we just left.
  useEffect(() => {
    if (!organization) return;

    selectTab(currentTabId, true);
  }, [currentTabId, organization, selectTab]);

  return (
    <Flex direction="column" padding="1rem 1.5rem" className="h-full">
      <Heading size="lg" marginBottom="1.5rem">
        Einstellungen
      </Heading>
      {organization ? (
        <Tabs
          position="relative"
          height="100%"
          flex="1"
          display="flex"
          flexDirection="column"
          // Panels mount when their tab is first opened. Previously some panels did this
          // by hand with a currentTab === 8 + (aiEnabled ? 1 : 0) comparison, which is
          // exactly the arithmetic this change exists to remove.
          isLazy
          index={currentTabIndex}
          onChange={(index) => {
            const tab = visibleTabs[index];
            if (tab) selectTab(tab.id, true);
          }}
        >
          <TabList overflowX="auto">
            {visibleTabs.map((tab) => (
              <Tab key={tab.id} sx={{ color: "#7C8080" }} className="text-nowrap h-full">
                {tab.label}
              </Tab>
            ))}
          </TabList>
          <TabPanels display="flex" flexDirection="column" flex="1">
            {visibleTabs.map((tab) => (
              <TabPanel key={tab.id} height="100%" padding="1.5rem 0" position="relative">
                {tab.id === "unternehmen" && (
                  <Flex className="px-8 py-4 rounded border border-gray-300 h-full flex-col gap-12 bg-white">
                    <CompanySettings organization={organization} setOrganization={setOrganization} />
                  </Flex>
                )}
                {tab.id === "email-signatur" && <EmailSignature />}
                {tab.id === "schnittstellen" && <ThirdPartyApps />}
                {tab.id === "rollen" && (
                  <>
                    <Button
                      position="absolute"
                      colorScheme="primary"
                      color="white"
                      className="flex gap-2 p-2 text-white absolute right-3 -top-24"
                      variant="solid"
                      onClick={() => {
                        navigate(`/nutzer/neu?tab=${tab.id}`);
                      }}
                    >
                      <Box
                        className="text-primary"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        borderRadius="50%"
                        bg="white"
                        boxSize="1rem"
                      >
                        <SmallAddIcon />
                      </Box>
                      <span>Benutzer einladen</span>
                    </Button>
                    <UserDataTable />
                  </>
                )}
                {tab.id === "datenschutz" && <PrivacyPolicy organization={organization} setOrganization={setOrganization} />}
                {tab.id === "agentur" && (
                  <Flex className="px-8 py-4 rounded border border-gray-300 h-full flex-col gap-12 bg-white">
                    <AgencyForm organization={organization} setOrganization={setOrganization} />
                  </Flex>
                )}
                {tab.id === "abo" && (
                  <Flex className="px-8 py-4 rounded border border-gray-300 h-full flex-col gap-6 bg-white">
                    <Text className="text-gray-400 text-sm">
                      Klicken Sie auf den Button und melde Sie sich an, um die Details Ihres Abos und die dazugehörigen
                      Rechnungen einzusehen
                    </Text>
                    <Link to={process.env.REACT_APP_STRIPE_CUSTOMER_PORTAL!} target="_blank">
                      <Button colorScheme="primary" className="flex gap-2 p-2 text-white w-fit" variant="solid">
                        Abonnement verwalten
                      </Button>
                    </Link>
                  </Flex>
                )}
                {tab.id === "benachrichtigungen" && (
                  <Flex className="px-8 py-4 rounded border border-gray-300 h-full flex-col gap-12 bg-white">
                    <InternalMail organization={organization} setOrganization={setOrganization} />
                  </Flex>
                )}
                {tab.id === "kandidat-ki" && (
                  <Flex className="px-8 py-4 rounded border border-gray-300 h-full flex-col gap-12 bg-white">
                    <KandidatKI organization={organization} setOrganization={setOrganization} />
                  </Flex>
                )}
                {tab.id === "conversion" && (
                  <Flex className="px-8 py-4 rounded border border-gray-300 h-full flex-col gap-12 bg-white">
                    <ConversionTracking />
                  </Flex>
                )}
                {tab.id === "webhooks" && (
                  <Flex className="px-8 py-4 rounded border border-gray-300 h-full flex-col gap-12 bg-white">
                    <Webhooks />
                  </Flex>
                )}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      ) : (
        <Flex className="justify-center w-full h-52 items-center">
          <Spinner size="xl" color="primary.500" />
        </Flex>
      )}
    </Flex>
  );
};

const AgencyForm = ({
  organization,
  setOrganization,
}: {
  organization: OrganizationResponseDto;
  setOrganization: Dispatch<SetStateAction<OrganizationResponseDto | undefined>>;
}) => {
  const { apiRequest } = useApiRequest();

  const formSchema = z.object({
    agencyEmail: z.string().email("Bitte geben Sie eine E-Mail Adresse ein"),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      agencyEmail: organization?.agencyEmail ?? "",
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    const res = await apiRequest<OrganizationResponseDto>(`organizations/`, "PATCH", {
      body: {
        agencyEmail: values.agencyEmail,
      },
      toast: { toastText: "Daten erfolgreich aktualisiert" },
    });

    if (res.data) {
      setOrganization({
        ...organization,
        ...res.data,
      });
    }
  }

  if (!organization) {
    return <Spinner />;
  } else {
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="w-full flex flex-col gap-8 h-full bg-white">
        <Flex className="h-full flex-col">
          <Text className="text-gray-400 text-sm">
            Wenn Sie mit einer Agentur für die Personalgewinnung zusammenarbeiten, die die Stellenanzeigen verwaltet,
            geben Sie hier die entsprechende E-Mail-Adresse ein. Die Agentur wird benachrichtigt, sobald Sie eine
            Stellenanzeige in kandidat.io aktivieren oder deaktivieren.
          </Text>
          <Box className="flex gap-2">
            <FormField
              control={form.control}
              name="agencyEmail"
              render={({ field }) => (
                <FormItem className="flex gap-4 mb-4 w-full grow">
                  <FormLabel className="max-w-52 w-full font-bold mt-4 text-md">
                    Agentur Email <span className="text-primary">*</span>
                  </FormLabel>
                  <Box className="w-full">
                    <FormControl>
                      <Input placeholder="Email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </Box>
                </FormItem>
              )}
            />
          </Box>
          <div className="h-full flex">
            <Text className="w-fit m-auto">
              Sie arbeiten noch mit keiner Agentur zusammen?
              <br />
              Werfen Sie doch einen Blick auf{" "}
              <Link to="https://freshtalents.de/" className="text-blue-500 hover:underline" target="_blank">
                www.freshtalents.de
              </Link>
            </Text>
          </div>
        </Flex>
        <Flex className="gap-4 justify-end mt-auto">
          <Button
            colorScheme="primary"
            color="white"
            className="flex gap-2 p-2 text-white ml-auto mt-4"
            variant="solid"
            type="submit"
          >
            <Box boxSize={5} display="flex" alignItems="center" justifyContent="center">
              <Save />
            </Box>
            <span>Speichern</span>
          </Button>
        </Flex>
      </form>
    </Form>
  );
};

export { Settings };
